import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each stat has a base value plus an optional live-increment cadence
// (in milliseconds) so the numbers feel alive after the count-up lands.
const DEFAULTS = {
  // Baseline pre-seeded on top of live DB counts (see backend /impact-stats)
  assessed: 11434,
  healthy: 7540,
  at_risk: 3894,
  precision_assessments: 1760,
  program_enrolments: 9,
  satisfaction: 100,
};

const STATS_META = [
  { key: "assessed",              apiKey: "assessed",              label: "Livers Assessed",                                       tickMs: 42_000, suffix: "+" },
  { key: "healthy",               apiKey: "healthy",               label: "Healthy Livers Identified",                             tickMs: 96_000, suffix: "+" },
  { key: "risk",                  apiKey: "at_risk",               label: "At-Risk Livers Identified Early",                      tickMs: 78_000, suffix: "+" },
  { key: "precision",             apiKey: "precision_assessments", label: "Precision Liver Evaluations Completed",                                suffix: "+" },
  { key: "program",               apiKey: "program_enrolments",    label: "Individuals Enrolled in Program",                                     suffix: "+" },
  { key: "sat",                   apiKey: "satisfaction",          label: "Patient Satisfaction",                                                suffix: "%" },
];

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Reusable animated counter: counts from 0 to `target` on view, then keeps
// ticking upward at `tickMs` intervals (unless tickMs is undefined).
function useLiveCount(target, tickMs) {
  const [value, setValue] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  // Count-up on entry
  useEffect(() => {
    if (!inView) return;
    let raf;
    const duration = 1600;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * easeOutCubic(t)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  // Slow live-tick after count-up finishes
  useEffect(() => {
    if (!inView || !tickMs) return;
    const id = setInterval(() => setValue((v) => v + 1), tickMs);
    return () => clearInterval(id);
  }, [inView, tickMs]);

  return { value, ref };
}

const Counter = ({ stat }) => {
  const { value, ref } = useLiveCount(stat.base, stat.tickMs);
  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      className="border-l border-white/15 pl-5"
      data-testid={`impact-stat-${stat.key}`}
    >
      <p className="font-display text-4xl font-medium leading-none tracking-tight text-white md:text-5xl">
        <span className="inline-flex items-baseline whitespace-nowrap">
          <span data-testid={`impact-count-${stat.key}`}>
            {value.toLocaleString()}
          </span>
          {stat.suffix ? (
            <span className="ml-0.5 text-sa-copper">{stat.suffix}</span>
          ) : null}
          {stat.tickMs ? (
            <span
              className="ml-2 inline-block h-1.5 w-1.5 -translate-y-4 rounded-full bg-emerald-400 opacity-80 animate-pulse"
              aria-hidden
            />
          ) : null}
        </span>
      </p>
      <p className="mt-3 text-sm leading-snug text-white/60">{stat.label}</p>
    </motion.div>
  );
};

export const ImpactStats = () => {
  const [values, setValues] = useState(DEFAULTS);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/impact-stats`)
      .then((r) => { if (alive) setValues({ ...DEFAULTS, ...r.data }); })
      .catch(() => { /* graceful fallback to defaults */ });
    return () => { alive = false; };
  }, []);
  const stats = STATS_META.map((m) => ({
    ...m,
    base: Math.max(0, values[m.apiKey] ?? DEFAULTS[m.apiKey] ?? 0),
    // Suppress the live "+1" ticker for the % satisfaction line
    tickMs: m.key === "sat" ? undefined : m.tickMs,
  }));
  return (
    <section
      id="impact"
      className="relative overflow-hidden bg-sa-forest py-12 md:py-16"
      data-testid="impact-section"
    >
      <div className="grain pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            Our impact
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl">
            Precision, measured in outcomes.
          </h2>
          <p className="mt-5 flex items-center gap-2 text-sm text-white/60">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live counters — updated continuously as new patients complete their journey.
          </p>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-6"
        >
          {stats.map((s) => (
            <Counter key={s.key} stat={s} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactStats;
